import { SUPABASE_URL } from '../lib/supabaseConfig'

/**
 * The photographs inside one photography deliverable's Drive folder.
 *
 * A deliverable assumes one file and photography is inherently many, which is
 * why a photography item used to fall through to a black box with an "Open
 * folder" button — the client was being asked to approve stills they could not
 * see (audit §2.4).
 *
 * NOTHING HERE NAMES A FOLDER. The request carries the client's own access
 * token and the deliverable's key, and the Edge Function resolves the folder
 * server-side from that deliverable's stored Drive link. If a folder id were a
 * parameter, one client link would be a key to the whole company drive. Do not
 * add one.
 *
 * The images themselves never pass through Supabase: the function returns
 * Drive file ids and the browser loads thumbnails straight from Drive's own
 * hosts (see driveImageUrl in utils/drive.ts).
 */

export interface Photo {
  id: string
  name: string
  /** Pixel dimensions where Drive knows them, so a tall frame can be shown tall. */
  width: number | null
  height: number | null
}

export interface Gallery {
  photos: Photo[]
  /** The Drive folder itself, for "download all" — Drive has no zip URL. */
  folderUrl: string
  /** True when the lookup itself failed, as opposed to a folder with no images. */
  failed: boolean
}

const EMPTY: Gallery = { photos: [], folderUrl: '', failed: false }

/** The access token the client arrived with. It is the whole credential. */
function accessToken(): string {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get('access') ?? ''
}

export async function fetchGallery(deliverableKey: string): Promise<Gallery> {
  const access = accessToken()
  if (!access || !deliverableKey) return EMPTY

  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/client-photos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access, deliverable: deliverableKey }),
    })
    const result = (await response.json().catch(() => ({}))) as Partial<Gallery>

    if (!response.ok) {
      console.warn(`[photos] gallery lookup failed (${response.status})`)
      return { photos: [], folderUrl: result.folderUrl ?? '', failed: true }
    }

    const photos = Array.isArray(result.photos) ? result.photos : []
    return {
      photos: photos.filter((photo) => Boolean(photo?.id)),
      folderUrl: result.folderUrl ?? '',
      failed: false,
    }
  } catch (error) {
    console.warn('[photos] gallery unreachable:', error)
    return { ...EMPTY, failed: true }
  }
}
